import type { Command, GridMetadata } from '../../menu/types';
import { GridType } from '../../menu/types';

/**
 * Resolve the effective command id used by the client.
 * BO custom commands often arrive as alias="custom" with the real action in command.action.
 */
export const getCommandId = (command: Command): string =>
  command.alias === 'custom' && command.action ? command.action.toLowerCase() : command.alias;

export const hasCommand = (commands: Command[] | undefined, commandId: string): boolean =>
  commands?.some((command) => getCommandId(command) === commandId) ?? false;

export const whenCommand = <T>(
  commands: Command[] | undefined,
  commandId: string,
  handler: T
): T | undefined => (hasCommand(commands, commandId) ? handler : undefined);

/**
 * Umbraco `zCmdDelete` -> alias `delete`. Used for row-level delete
 * (`gridOptions.editing.allowDeleting`), not as a custom toolbar button
 * (toolbar only adds group commands, action commands, and named built-ins).
 */
export const isRowDeleteCommand = (command: Command): boolean =>
  command.alias === 'delete' && !command.isGroup && !command.action;

/**
 * Group-level "delete selected" command (e.g. Umbraco command with
 * action="deleteEntities", isGroup=true). When present, deletion happens via
 *  the checkbox multi-select + toolbar button, so the per-row delete icon in the
 * actions column should be hidden to avoid offering two delete paths.
 */
export const hasGroupDeleteCommand = (commands: Command[] | undefined): boolean =>
  commands?.some((command) => command.isGroup && getCommandId(command) === 'deleteentities') ??
  false;

/**
 * Detail-form grids expose row navigation through the _navigate column rather
 * than the toolbar. An explicit inline-edit command takes precedence.
 */
export const isDetailNavigationGrid = (metadata: GridMetadata, commands?: Command[]): boolean => {
  if (metadata.gridType === GridType.GridWithDetail) return true;

  const hasDetailForm = metadata.detailFormId != null || metadata.detailFormKey != null;
  const editCommand = commands?.find((command) => getCommandId(command) === 'edit');

  return hasDetailForm && editCommand?.isInlineEdit !== true;
};
